import React from "react";
import { Box, Button, Flex, Heading, Image, Text } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";
import ReducedTopbar from "../../../../components/utils/ReducedTopbar";

const MotionBox = motion.create(Box);
const MotionImage = motion.create(Image);

export default function MascotScreen({ image, imageAlt = "Wako, mascota de Arawaks", eyebrow, title, description, onDone, duration, actionLabel = "Continuar ahora", children, celebrating = false, badge, showCountdown = false }) {
  const reduceMotion = useReducedMotion();
  const [remaining, setRemaining] = React.useState(duration ? Math.ceil(duration / 1000) : null);

  React.useEffect(() => {
    if (!duration) return undefined;
    const timer = window.setTimeout(onDone, duration);
    return () => window.clearTimeout(timer);
  }, [duration, onDone]);

  React.useEffect(() => {
    if (!duration || !showCountdown) return undefined;
    setRemaining(Math.ceil(duration / 1000));
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const next = Math.max(0, Math.ceil((duration - (Date.now() - startedAt)) / 1000));
      setRemaining(next);
    }, 200);
    return () => window.clearInterval(timer);
  }, [duration, showCountdown]);

  return <Flex minH="100dvh" position="relative" overflow="hidden" px={{ base: "6", lg: "12" }} py="10" bg="public.bg.canvas" color="public.fg.default" align="center" justify="center">
    <Box position="absolute" boxSize={{ base: "220px", lg: "360px" }} rounded="full" bg="#dce8f1" _dark={{ bg: "#243746" }} right={{ base: "-130px", lg: "-100px" }} top={{ base: "12%", lg: "-100px" }} opacity=".72"/>
    <Box position="absolute" w={{ base: "230px", lg: "380px" }} h={{ base: "100px", lg: "170px" }} rounded="full" bg="#eadde8" _dark={{ bg: "#3d3040" }} left={{ base: "-130px", lg: "35%" }} bottom={{ base: "4%", lg: "-100px" }} transform="rotate(-16deg)" opacity=".66"/>
    <ReducedTopbar showLogout/>
    {children}
    <MotionBox role="status" aria-live="polite" position="relative" zIndex="1" textAlign={{ base: "center", lg: "left" }} w="full" maxW={{ base: "520px", lg: "960px" }} display={{ lg: "grid" }} gridTemplateColumns={{ lg: "minmax(0, 1fr) minmax(320px, .9fr)" }} alignItems="center" gap={{ lg: "12" }} initial={reduceMotion ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
      <Box h={{ base: "280px", md: "330px", lg: "300px" }} position="relative">
        <MotionBox position="absolute" inset="5% 8%" rounded="full" bg="linear-gradient(145deg, #dce8f1, #eadde8)" _dark={{ bg: "linear-gradient(145deg, #243746, #3d3040)" }} boxShadow="0 24px 60px rgba(64,74,96,.18)" animate={reduceMotion ? undefined : { scale: [1, 1.04, 1] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}/>
        <MotionImage
          src={image} alt={imageAlt} position="relative" h="full" w="full" objectFit="contain"
          filter="drop-shadow(0 22px 28px rgba(127,29,29,.14))"
          animate={reduceMotion ? undefined : celebrating
            ? { y: [0, -18, 0, -8, 0], rotate: [0, -2, 2, -1, 0], scale: [1, 1.06, 1, 1.03, 1] }
            : { y: [0, -7, 0], rotate: [0, 1, 0] }}
          transition={celebrating
            ? { duration: 1.5, repeat: Infinity, repeatDelay: 0.35, ease: "easeInOut" }
            : { duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
        />
      </Box>
      <Box>
      {badge && <MotionBox display="inline-flex" mt={{ base: "4", lg: "0" }} px="5" py="2" rounded="12px" border="3px solid" borderColor="green.500" color="green.700" bg="green.50" _dark={{ color: "green.200", bg: "green.950" }} fontSize="sm" fontWeight="900" letterSpacing="wide" textTransform="uppercase" initial={reduceMotion ? false : { opacity: 0, scale: 1.7, rotate: -9 }} animate={{ opacity: 1, scale: 1, rotate: -3 }} transition={{ type: "spring", stiffness: 240, damping: 15, delay: 0.2 }}>{badge}</MotionBox>}
      <Text mt="5" color="brand.red" fontSize="sm" fontWeight="800" letterSpacing="wide" textTransform="uppercase">{eyebrow}</Text>
      <Heading mt="2" fontSize={{ base: "32px", md: "42px" }} lineHeight="1.08">{title}</Heading>
      <Text mt="4" mx={{ base: "auto", lg: "0" }} maxW="420px" color="public.fg.muted" lineHeight="1.7">{description}</Text>
      {showCountdown && remaining !== null && <Text mt="3" color="public.fg.default" fontSize="sm" fontWeight="700">Abriendo tu perfil en {remaining}...</Text>}
      {duration && <Box mt="8" h="4px" overflow="hidden" rounded="full" bg="red.100">
        <MotionBox h="full" bg="brand.red" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: duration / 1000, ease: "linear" }} transformOrigin="left"/>
      </Box>}
      <Button mt={duration ? "5" : "8"} h={{ base: "54px", lg: "48px" }} px="9" rounded="full" bg="brand.red" color="gray.50" fontWeight="800" _hover={{ bg: "red.700" }} onClick={onDone}>{actionLabel}<FiArrowRight/></Button>
      </Box>
    </MotionBox>
  </Flex>;
}
